import { z } from 'zod';

// Connection status types
export type ConnectionStatus = 'connected' | 'disconnected' | 'error' | 'pending';

// Authentication method types
export type AuthMethod = 'oauth2' | 'apikey' | 'basic' | 'custom';

// Error types for CRM integration
export enum CrmErrorType {
  CONNECTION_FAILED = 'CONNECTION_FAILED',
  AUTHENTICATION_FAILED = 'AUTHENTICATION_FAILED',
  RATE_LIMITED = 'RATE_LIMITED',
  TIMEOUT = 'TIMEOUT',
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  INVALID_CONFIGURATION = 'INVALID_CONFIGURATION',
  DATA_SYNC_ERROR = 'DATA_SYNC_ERROR',
  UNKNOWN = 'UNKNOWN'
}

// Base connection configuration
export interface ConnectionConfig {
  id: string;
  name: string;
  provider: string;
  url: string;
  authMethod: AuthMethod;
  enabled: boolean;
  timeout?: number;
  retryAttempts?: number;
  retryDelay?: number;
  headers?: Record<string, string>;
  parameters?: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

// OAuth2 specific configuration
export interface OAuth2Config extends ConnectionConfig {
  authMethod: 'oauth2';
  clientId: string;
  clientSecret?: string;
  authorizationUrl: string;
  tokenUrl: string;
  redirectUri: string;
  scope: string;
  accessToken?: string;
  refreshToken?: string;
  tokenExpiry?: string;
}

// API Key specific configuration
export interface ApiKeyConfig extends ConnectionConfig {
  authMethod: 'apikey';
  apiKey?: string;
  apiKeyHeader?: string;
  apiKeyQueryParam?: string;
}

// Basic auth specific configuration
export interface BasicAuthConfig extends ConnectionConfig {
  authMethod: 'basic';
  username?: string;
  password?: string;
}

// Custom auth specific configuration
export interface CustomAuthConfig extends ConnectionConfig {
  authMethod: 'custom';
  customAuthData?: Record<string, any>;
}

// Union type for all connection configurations
export type CrmConnectionConfig = 
  | OAuth2Config 
  | ApiKeyConfig 
  | BasicAuthConfig 
  | CustomAuthConfig;

// Field mapping configuration
export interface FieldMapping {
  id: string;
  connectionId: string;
  sourceField: string;
  targetField: string;
  direction: 'inbound' | 'outbound' | 'bidirectional';
  transformation?: string;
  enabled: boolean;
}

// Connection status information
export interface ConnectionStatus {
  id: string;
  connectionId: string;
  status: 'connected' | 'disconnected' | 'error' | 'pending';
  lastSyncTime?: string;
  lastError?: {
    type: CrmErrorType;
    message: string;
    timestamp: string;
    details?: any;
  };
  metrics?: {
    successfulSyncs: number;
    failedSyncs: number;
    totalRecordsSynced: number;
    averageSyncTime: number;
  };
}

// Validation schemas
export const connectionConfigSchema = z.object({
  name: z.string().min(1, "Name is required"),
  provider: z.string().min(1, "Provider is required"),
  url: z.string().url("Must be a valid URL"),
  authMethod: z.enum(["oauth2", "apikey", "basic", "custom"]),
  enabled: z.boolean(),
  timeout: z.number().min(1000).max(60000).optional(),
  retryAttempts: z.number().min(0).max(10).optional(),
  retryDelay: z.number().min(100).max(10000).optional(),
  headers: z.record(z.string()).optional(),
  parameters: z.record(z.string()).optional()
});

export const oauth2ConfigSchema = connectionConfigSchema.extend({
  authMethod: z.literal("oauth2"),
  clientId: z.string().min(1, "Client ID is required"),
  clientSecret: z.string().optional(),
  authorizationUrl: z.string().url("Must be a valid URL"),
  tokenUrl: z.string().url("Must be a valid URL"),
  redirectUri: z.string().url("Must be a valid URL"),
  scope: z.string()
});

export const apiKeyConfigSchema = connectionConfigSchema.extend({
  authMethod: z.literal("apikey"),
  apiKey: z.string().optional(),
  apiKeyHeader: z.string().optional(),
  apiKeyQueryParam: z.string().optional()
});

export const basicAuthConfigSchema = connectionConfigSchema.extend({
  authMethod: z.literal("basic"),
  username: z.string().optional(),
  password: z.string().optional()
});

export const customAuthConfigSchema = connectionConfigSchema.extend({
  authMethod: z.literal("custom"),
  customAuthData: z.record(z.any()).optional()
});

export const fieldMappingSchema = z.object({
  sourceField: z.string().min(1, "Source field is required"),
  targetField: z.string().min(1, "Target field is required"),
  direction: z.enum(["inbound", "outbound", "bidirectional"]),
  transformation: z.string().optional(),
  enabled: z.boolean()
});